import { useEffect, useRef, useState, useCallback } from 'react';
import { Key, ChevronDown, HelpCircle, Flame, TrendingUp, TrendingDown, Minus, Pin, PinOff, Triangle as TriangleIcon, AlertTriangle, Flag, Zap } from 'lucide-react';
import InlinePopover from '@/components/InlinePopover';

/**
 * BadgeKey — compact dropdown explaining every badge / pill / abbrev
 * the app shows. Designed to live next to the table so users can
 * decode any unfamiliar badge in one tap.
 *
 * Two ways to invoke it:
 *   1. <BadgeKey />                              — full key, generic trigger.
 *   2. <BadgeHelpIcon badgeKey="leveraged" />    — tiny "?" trigger that
 *      opens the SAME content scrolled to (and briefly highlighting)
 *      the named row. Used inline next to mystery badges like
 *      LEVERAGED, CEF, BDC etc on the Stock Modal.
 *
 * On first viewport mount (per browser, tracked via localStorage flag
 * `bk_seen`) the trigger pill plays a one-shot amber pulse so existing
 * users notice the new dropdown. The flag is set as soon as the
 * animation fires so the second visit is silent.
 *
 * Pin-to-top: each row has a pin icon. Pinned rows appear in a
 * compact "Pinned" section at the very top of the content so heavy
 * users (LEVERAGED / CEF / BDC) don't have to scroll the long key
 * each time. Pins persist in localStorage under `bk_pins`.
 */

// ─── Pin storage helpers ──────────────────────────────────────────────
const PIN_STORAGE_KEY = 'bk_pins';

function loadPins() {
  try {
    const raw = localStorage.getItem(PIN_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function savePins(ids) {
  try { localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(ids)); } catch { /* ignore */ }
}

export default function BadgeKey() {
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem('bk_seen')) {
        setPulse(true);
        // Mark seen straight away so we never animate twice across reloads.
        localStorage.setItem('bk_seen', '1');
      }
    } catch {
      // Private mode / disabled storage — no pulse rather than spam every mount.
    }
  }, []);
  return (
    <InlinePopover
      testId="badge-key"
      maxWidth={360}
      ariaLabel="Open badge key"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/40 hover:bg-muted/60 text-xs font-semibold text-foreground border border-border/60 ${pulse ? 'animate-badge-key-pulse ring-1 ring-amber-400/60' : ''}`}
      render={() => <BadgeKeyContent />}
    >
      <Key className="w-3.5 h-3.5 text-amber-500" />
      <span>Badge Key</span>
      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
    </InlinePopover>
  );
}

/**
 * Tiny "?" icon that opens the Badge Key directly to a specific entry.
 * Place inline next to a badge on the Stock Modal to give users a
 * one-tap way to learn what that badge means.
 *
 * Props:
 *   • badgeKey — one of the row keys defined below (king, aristocrat,
 *     achiever, quality_elite, quality_average, quality_risky,
 *     leveraged, cef, bdc, reit, futures, cut, raise, risk).
 */
export function BadgeHelpIcon({ badgeKey, className = '', testIdSuffix = '' }) {
  return (
    <InlinePopover
      testId={testIdSuffix ? `badge-help-${badgeKey}-${testIdSuffix}` : `badge-help-${badgeKey}`}
      maxWidth={360}
      ariaLabel="Show badge meaning"
      title="Click to see what this badge means"
      className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-muted-foreground/80 hover:text-amber-500 hover:bg-amber-500/10 transition-colors align-middle ${className}`}
      render={() => <BadgeKeyContent focusKey={badgeKey} />}
    >
      <HelpCircle className="w-3 h-3" />
    </InlinePopover>
  );
}

function Row({ id, swatch, name, def, focused, isPinned, onTogglePin }) {
  const ref = useRef(null);
  // When this row is the focus target, scroll it into view + briefly
  // pulse-highlight so the user's eye lands on it immediately.
  useEffect(() => {
    if (!focused) return;
    const el = ref.current;
    if (!el) return;
    // Slight delay so the popover's own positioning math finishes first.
    const t = setTimeout(() => {
      try { el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch { el.scrollIntoView(); }
    }, 50);
    return () => clearTimeout(t);
  }, [focused]);
  return (
    <div
      ref={ref}
      data-badge-key-row={id}
      className={`flex items-start gap-2 py-1.5 border-b border-border/30 last:border-b-0 transition-colors ${focused ? 'bg-amber-500/10 -mx-1 px-1 rounded' : ''}`}
    >
      <div className="shrink-0 w-24 flex justify-center pt-0.5">{swatch}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold text-foreground break-words flex items-center gap-1">
          <span className="flex-1 min-w-0">{name}</span>
          {onTogglePin ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onTogglePin(id); }}
              className={`shrink-0 inline-flex items-center justify-center w-4 h-4 rounded transition-colors ${isPinned ? 'text-amber-500 hover:text-amber-600' : 'text-muted-foreground/40 hover:text-amber-500'}`}
              aria-label={isPinned ? `Unpin ${name}` : `Pin ${name} to top`}
              title={isPinned ? 'Unpin from top' : 'Pin to top'}
              data-testid={`badge-key-pin-${id}`}
            >
              {isPinned ? <Pin className="w-3 h-3 fill-current" /> : <PinOff className="w-3 h-3" />}
            </button>
          ) : null}
        </div>
        <div className="text-[10px] text-muted-foreground leading-snug break-words">{def}</div>
      </div>
    </div>
  );
}

const tierSwatch = (emoji) => (
  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold">{emoji}</span>
);
const qualitySwatch = (label, tone) => {
  const map = {
    emerald: 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 ring-emerald-400/40',
    amber: 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 ring-amber-400/40',
    rose: 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 ring-rose-400/40',
  };
  const dot = tone === 'emerald' ? 'bg-emerald-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ring-1 text-[9px] font-bold uppercase ${map[tone]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
};
const flagSwatch = (label, color) => (
  <span className={`inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[9px] font-bold uppercase ${color}`}>
    {label}
  </span>
);

// Preferred-share chip palettes — mirror PreferredCells / PreferredCallChip exactly.
const PREF = {
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-700/60',
  emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-700/60',
  rose: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-300/60 dark:border-rose-700/60',
  sky: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300/60 dark:border-sky-700/60',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/60 dark:border-indigo-700/60',
  callAmber: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-400/60',
  callRed: 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border border-red-400/60',
  muted: 'bg-muted text-muted-foreground border border-border',
};

const streakSwatch = (
  <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[9px] font-bold uppercase bg-amber-100 dark:bg-amber-950/60 text-amber-900 dark:text-amber-200 ring-1 ring-amber-400/60">
    <TrendingUp className="w-2 h-2" />
    +X%
    <Flame className="w-2 h-2 text-orange-500 ml-0.5" />
  </span>
);

const triangleSwatch = (label, tone, trap = false, partial = false) => {
  const map = {
    emerald: 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60',
    sky: 'bg-sky-100 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60',
    amber: 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60',
    rose: 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60',
  };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase border ${partial ? 'border-dashed' : ''} ${map[tone]}`}>
      <TriangleIcon className="w-2.5 h-2.5" />
      {label}
      {trap && <AlertTriangle className="w-2.5 h-2.5 ml-0.5" />}
    </span>
  );
};

// ─── Capital Analytics rank pill (matches CapitalScoreBadge.jsx shape) ───
// Non-div stocks surface the 4-factor Capital Analytics rank instead of
// the dividend Quality Score. Same dot + score + short-label shape but
// with a directional icon (▲ / — / ▼) driving the verdict palette.
const capitalRankSwatch = (rank, verdict) => {
  const palette = {
    BUY:   { bg: 'bg-emerald-100 dark:bg-emerald-950/60', text: 'text-emerald-800 dark:text-emerald-300', ring: 'ring-emerald-300/60 dark:ring-emerald-700/60', dot: 'bg-emerald-500', Icon: TrendingUp },
    WATCH: { bg: 'bg-amber-100 dark:bg-amber-950/60',   text: 'text-amber-800 dark:text-amber-300',   ring: 'ring-amber-300/60 dark:ring-amber-700/60',   dot: 'bg-amber-500',   Icon: Minus },
    AVOID: { bg: 'bg-rose-100 dark:bg-rose-950/60',     text: 'text-rose-800 dark:text-rose-300',     ring: 'ring-rose-300/60 dark:ring-rose-700/60',     dot: 'bg-rose-500',     Icon: TrendingDown },
  }[verdict];
  const Icon = palette.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase tracking-wider ${palette.bg} ${palette.text} ${palette.ring}`}>
      <span className={`rounded-full w-2 h-2 ${palette.dot}`} />
      <Icon className="w-2.5 h-2.5" />
      {rank}
      <span className="opacity-70 font-mono">{verdict}</span>
    </span>
  );
};

// ─── Altman Z zone chip (matches Screener non-div Altman Z column) ──────
// Bankruptcy risk model. Z > 2.99 = SAFE / 1.81 < Z ≤ 2.99 = GRAY /
// Z ≤ 1.81 = DISTRESS. Rendered as dot + numeric Z + zone label.
const altmanSwatch = (score, label) => {
  const map = {
    SAFE:     { bg: 'bg-emerald-500/15 dark:bg-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-500/40', dot: 'bg-emerald-500' },
    GRAY:     { bg: 'bg-amber-500/15 dark:bg-amber-500/20',     text: 'text-amber-700 dark:text-amber-300',     ring: 'ring-amber-500/40',     dot: 'bg-amber-500' },
    DISTRESS: { bg: 'bg-rose-500/15 dark:bg-rose-500/20',       text: 'text-rose-700 dark:text-rose-300',       ring: 'ring-rose-500/40',       dot: 'bg-rose-500' },
  }[label];
  return (
    <span className={`inline-flex items-center gap-0.5 px-1 py-px rounded-full ring-1 text-[9px] font-bold uppercase tracking-wider ${map.bg} ${map.text} ${map.ring}`}>
      <span className={`rounded-full w-1.5 h-1.5 ${map.dot}`} />
      {score}
      <span className="opacity-70 font-mono">{label}</span>
    </span>
  );
};

// ─── Founder Stake chip (matches Screener founder-stake badge) ──────────
// Form 3 Initial Insider position parked at ≥ founder-tier threshold.
// Rendered inline next to a ticker with a small flag icon.
const founderSwatch = (
  <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300/60 dark:border-sky-700/60">
    <Flag className="w-2.5 h-2.5" />
    Founder
  </span>
);

// ─── Single source of truth for every row, grouped by section ────────
const BADGE_SECTIONS = [
  {
    title: 'Tier Badges',
    rows: [
      { id: 'king', swatch: tierSwatch('\u{1F451}'), name: 'King',
        def: '50+ consecutive years of dividend increases. The most elite tier — fewer than 50 stocks qualify.' },
      { id: 'aristocrat', swatch: tierSwatch('\u{1F3C6}'), name: 'Aristocrat',
        def: '25+ years of dividend increases AND a member of the S&P 500.' },
      { id: 'achiever', swatch: tierSwatch('\u{1F396}\uFE0F'), name: 'Achiever',
        def: '10+ years of consecutive dividend increases (NASDAQ Dividend Achievers index).' },
    ],
  },
  {
    title: 'Dividend Quality Score',
    rows: [
      { id: 'quality_elite', swatch: qualitySwatch('A+ 90', 'emerald'), name: 'Elite (85+)',
        def: 'Top-tier safety, sweet-spot yield, growing dividend, and a long streak. Aristocrat / King grade.' },
      { id: 'quality_average', swatch: qualitySwatch('C 45', 'amber'), name: 'Average (40–54)',
        def: 'Some pillars OK, others lagging. Worth a closer look at the Stock Modal breakdown.' },
      { id: 'quality_risky', swatch: qualitySwatch('F 12', 'rose'), name: 'Risky (under 25)',
        def: 'Yield trap, stretched payout, leveraged structure, or no track record. Treat with caution.' },
    ],
  },
  {
    title: 'Non-Dividend Quality Tier',
    subtitle: '· shown when Stock Table is on All or Non-Dividend',
    rows: [
      { id: 'non_div_growth_s', swatch: flagSwatch('G-S', 'bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 ring-1 ring-emerald-500/40'),
        name: 'GROWTH-S (85+)',
        def: 'Top-tier non-dividend growth — strong revenue + EPS CAGR, positive FCF, disciplined leverage, reasonable valuation. NVDA-class names.' },
      { id: 'non_div_growth_a', swatch: flagSwatch('G-A', 'bg-sky-500/20 text-sky-800 dark:text-sky-300 ring-1 ring-sky-500/40'),
        name: 'GROWTH-A (70-84)',
        def: 'Strong all-around growth name with one or two minor weaknesses.' },
      { id: 'non_div_growth_b', swatch: flagSwatch('G-B', 'bg-blue-500/20 text-blue-800 dark:text-blue-300 ring-1 ring-blue-500/40'),
        name: 'GROWTH-B (55-69)',
        def: 'Solid quality but with one clearly weak signal (margin trend, leverage, or valuation).' },
      { id: 'non_div_growth_c', swatch: flagSwatch('G-C', 'bg-amber-500/20 text-amber-800 dark:text-amber-300 ring-1 ring-amber-500/40'),
        name: 'GROWTH-C (40-54)',
        def: 'Mixed quality — watch the modal breakdown to see which signal pulled the score down.' },
      { id: 'non_div_spec', swatch: flagSwatch('SPEC', 'bg-rose-500/15 text-rose-800 dark:text-rose-300 ring-1 ring-rose-500/30'),
        name: 'SPECULATIVE (under 40)',
        def: 'Story stock / pre-revenue / heavy leverage / lossmaking. Common for biotechs and freshly-IPOed micro-caps.' },
    ],
  },
  {
    title: 'Dividend Triangle Score',
    subtitle: '· appears after first Triangle view',
    rows: [
      { id: 'triangle_strong', swatch: triangleSwatch('89', 'emerald'), name: 'Strong (70+)',
        def: 'Revenue, EPS, and Dividend are all growing at a healthy compound rate (5-yr CAGR). Click the chip on any stock row to open the full radar at /triangle/TICKER.' },
      { id: 'triangle_healthy', swatch: triangleSwatch('63', 'sky'), name: 'Healthy (50-69)',
        def: 'Two of the three legs are growing solidly; one is lagging. Most quality dividend payers land here.' },
      { id: 'triangle_mixed', swatch: triangleSwatch('42', 'amber'), name: 'Mixed (35-49)',
        def: 'At least one leg is flat or shrinking. Worth opening the Triangle tab to see which one and why.' },
      { id: 'triangle_partial', swatch: triangleSwatch('13', 'rose', false, true), name: 'Partial (ETF / lite data)',
        def: 'DASHED-border chip — only 1 or 2 of the 3 triangle legs had real CAGR data when the score was computed (usually ETFs and freshly-listed tickers where Revenue and/or EPS aren\'t reported at the fund level). Treat as approximate. Colour still follows the same score band (emerald / sky / amber / rose), but the dashed outline says "the number is a partial signal, not a full radar." Click the chip to open the Triangle tab and see which legs are missing.' },
      { id: 'triangle_trap', swatch: triangleSwatch('31', 'rose', true), name: 'Trap / Weak (under 35)',
        def: "Either a low composite score OR the dividend is growing materially faster than Revenue or EPS — classic dividend-trap signature. The ⚠ icon means our trap detector fired; hover the chip for the specific reason. Always pair with the Quality Score before buying." },
    ],
  },
  {
    title: 'Capital Analytics Rank',
    subtitle: '· non-dividend stocks · 4-factor equity-quality engine',
    rows: [
      { id: 'capital_buy', swatch: capitalRankSwatch(88, 'BUY'), name: 'BUY (rank 70+)',
        def: 'Top-quartile rank on the 4-factor Capital Analytics model (buyback yield, CFROI, momentum, Altman Z). Click the chip on any non-div row to open the full Capital Analytics tab with per-factor Z-scores.' },
      { id: 'capital_watch', swatch: capitalRankSwatch(52, 'WATCH'), name: 'WATCH (rank 35-69)',
        def: 'Mid-pack quality — one or two factors are dragging the composite. Open the tab to see whether it\'s dilution, weak returns on capital, momentum, or balance-sheet health.' },
      { id: 'capital_avoid', swatch: capitalRankSwatch(18, 'AVOID'), name: 'AVOID (rank under 35)',
        def: 'Bottom-quartile composite. Typically heavy dilution, negative CFROI, or a Distress-zone Altman Z. Story stocks / turnarounds live here — verify the thesis before buying.' },
    ],
  },
  {
    title: 'Altman Z-Score Zone',
    subtitle: '· non-dividend Stock Table column · bankruptcy risk model',
    rows: [
      { id: 'altman_safe', swatch: altmanSwatch('9.09', 'SAFE'), name: 'SAFE (Z > 2.99)',
        def: 'Balance sheet is comfortably outside the bankruptcy-risk zone based on Altman\'s five-factor Z formula. Higher = safer. Non-div compounders and growth names typically land here.' },
      { id: 'altman_gray', swatch: altmanSwatch('2.30', 'GRAY'), name: 'GRAY (1.81 < Z ≤ 2.99)',
        def: 'Ambiguous zone — the model can\'t confidently classify. Pair with the Capital Analytics rank + free cash flow trend before assuming safety.' },
      { id: 'altman_distress', swatch: altmanSwatch('0.62', 'DISTRESS'), name: 'DISTRESS (Z ≤ 1.81)',
        def: 'Historical bankruptcy zone. Not a guarantee of failure — the model is famously noisy on tech/biotech capital structures — but a strong "verify the thesis" signal. Common for money-losing biotechs, pre-revenue names, and heavily-leveraged turnarounds.' },
    ],
  },
  {
    title: 'Founder Stake',
    subtitle: '· Form 3 initial insider position at founder-tier size',
    rows: [
      { id: 'founder_stake', swatch: founderSwatch, name: 'Founder',
        def: 'Detected when a Form 3 (initial insider filing) shows a top holder parked at a founder-scale position (large baseline shares and/or a meaningful % of float). Click the badge to open the Insider Activity tab and see the full Initial Insider Positions table.' },
    ],
  },
  {
    title: 'Minervini SEPA',
    subtitle: '· shown while the Minervini SEPA Lite strategy preset is on · tap a badge for its breakdown',
    rows: [
      { id: 'minervini_rs', swatch: flagSwatch('RS 99', 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300/60 dark:border-sky-700/60'),
        name: 'RS rank (1–99)',
        def: 'Relative-strength percentile across every non-dividend common stock DiviDrip scans nightly — IBD-style, the 3-month return counted twice plus the 6-, 9- and 12-month returns. 99 is the strongest. Minervini\u2019s Trend Template requires 70+; the list sorts by it. Tap the badge to see the stock\u2019s 8 Trend Template rules, distance from its 52-week high and latest-quarter EPS growth.' },
      { id: 'minervini_tight', swatch: flagSwatch('Tight base', 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-700/60'),
        name: 'Tight base (VCP proxy)',
        def: 'The last 10 days\u2019 price range is at most 55% of the 50-day range AND 10-day average volume is at most 80% of its 50-day average — the measurable signature of a Volatility Contraction Pattern. A candidate flag only: count the contractions on the chart before treating it as a VCP. Not required to appear in the list.' },
    ],
  },
  {
    title: 'Money Market Funds',
    subtitle: '· type badge on MMF rows and in the Stock Modal header',
    rows: [
      { id: 'mmf_government', swatch: flagSwatch('GOVERNMENT', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 ring-1 ring-emerald-400/60'),
        name: 'Government MMF',
        def: 'Holds at least 99.5% cash, U.S. Treasuries, agency debt and government-backed repo. The default brokerage sweep type (SPAXX, VMFXX, SWVXX-style). Stable $1.00 NAV, no liquidity fees or gates; interest is federally taxable and partly state-taxable depending on the agency mix.' },
      { id: 'mmf_treasury', swatch: flagSwatch('TREASURY', 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 ring-1 ring-sky-400/60'),
        name: 'Treasury MMF',
        def: 'Only U.S. Treasury bills / notes (and Treasury repo). The lowest credit risk you can buy, and the interest is generally exempt from state and local income tax — the go-to in high-tax states. Yield usually runs a few basis points under a Government fund.' },
      { id: 'mmf_prime', swatch: flagSwatch('PRIME', 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 ring-1 ring-amber-400/60'),
        name: 'Prime MMF',
        def: 'Adds bank CDs, commercial paper and corporate short-term debt for a slightly higher 7-day yield. Carries a little credit risk; institutional prime funds float their NAV and can impose liquidity fees under SEC rule 2a-7. Fully taxable.' },
      { id: 'mmf_municipal', swatch: flagSwatch('MUNICIPAL', 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300 ring-1 ring-violet-400/60'),
        name: 'Municipal MMF',
        def: 'Short-term tax-exempt municipal paper. Interest is free of federal income tax (single-state versions are also state-tax-free for residents), so the lower headline yield can beat a taxable fund after tax for high brackets. May be subject to AMT.' },
    ],
  },
  {
    title: 'Preferred Shares & Baby Bonds',
    subtitle: '· Screener "Preferred Shares" view (Call Date + Type columns), Stock Modal header, Watchlist / Portfolio rows',
    rows: [
      { id: 'pref_preferred', swatch: flagSwatch('PREFERRED', PREF.amber),
        name: 'Preferred Share',
        def: 'A fixed-income share that pays a set coupon on a par value (usually $25) ahead of the common stock, with no vote and little price upside. The 5th letter of the ticker (STRRP, WVVIP) or a -P / -PR suffix marks the series. The Stock Modal header shows it with the cumulative status when known; the Insights tab carries the full Asset Class card.' },
      { id: 'pref_cumulative', swatch: flagSwatch('CUMULATIVE', PREF.emerald),
        name: 'Cumulative',
        def: 'Any skipped dividend accrues as arrears and must be paid in full before the common stock can receive a cent. The safer structure — most REIT, BDC and CEF preferreds are cumulative.' },
      { id: 'pref_non_cum', swatch: flagSwatch('NON-CUM', PREF.rose),
        name: 'Non-Cumulative',
        def: 'A skipped dividend is gone for good — the issuer owes you nothing later. Standard for bank and insurance preferreds (regulators require it for Tier-1 capital). Demand a higher coupon or a stronger issuer for this structure.' },
      { id: 'pref_fixed_float', swatch: flagSwatch('FIXED→FLOAT', PREF.sky),
        name: 'Fixed-to-Floating',
        def: 'Pays a fixed coupon until the first call date, then resets to a benchmark (3-month SOFR or 5-year Treasury) plus a stated spread. The Asset Class card shows the benchmark and spread when confirmed from the prospectus. If rates have fallen by the reset, the coupon can drop sharply — and the issuer often calls instead.' },
      { id: 'pref_variable', swatch: flagSwatch('VARIABLE', PREF.sky),
        name: 'Variable Rate',
        def: 'The payout is not fixed — it floats with a benchmark or with the issuer\'s declared rate from day one (treasury-vehicle style). Yield shown is the current annualised rate, not a promised coupon.' },
      { id: 'pref_term', swatch: flagSwatch('TERM 2031', PREF.amber),
        name: 'Term Preferred / Baby Bond Maturity',
        def: 'Has a mandatory redemption year — the issuer MUST return par on that date (OXLCM 2031, most baby bonds). Price gravitates toward par as the year approaches, which caps both the downside and the upside; buying below par locks in a pull-to-par gain.' },
      { id: 'pref_treasury', swatch: flagSwatch('TREASURY', PREF.indigo),
        name: 'Treasury Vehicle',
        def: 'A preferred issued by a company whose balance sheet is mostly a single asset (Strategy Inc.\'s bitcoin treasury — STRC, STRF, STRK, STRD). Coupon security depends on that asset\'s price and the issuer\'s ability to raise fresh capital, not on operating cash flow. Par is often $100 rather than $25.' },
      { id: 'pref_callable_now', swatch: flagSwatch('CALLABLE NOW', PREF.callAmber),
        name: 'Callable Now (trading below par)',
        def: 'Call protection has ended — the issuer may redeem at par with 30 days notice at any time. Because the share trades BELOW par, a call would actually pay you the gap, so this is upside risk, not downside. Yield to Call shows that instant gain as "+x% NOW".' },
      { id: 'pref_callable_above_par', swatch: flagSwatch('⚠ CALLABLE NOW', PREF.callRed),
        name: 'Callable Now — Above Par (call risk)',
        def: 'Call protection has ended AND the share trades above par. The issuer can redeem at par tomorrow and hand you an instant loss equal to the premium you paid. Yield to Call turns red ("−x% NOW"). Watchlist and Portfolio rows show the same red chip; the Portfolio summary totals this exposure across your holdings.' },
      { id: 'pref_call_above_par', swatch: flagSwatch('ABOVE PAR', PREF.callAmber),
        name: 'Above Par with a Future Call Date',
        def: 'Trades above par today while call protection still runs. The premium is safe until the call date, then at risk. Yield to Call (amber when below current yield) tells you what you really earn if the issuer redeems on day one.' },
      { id: 'pref_issuer_choice', swatch: flagSwatch("ISSUER'S CHOICE", PREF.muted),
        name: 'Redeemable at Issuer\'s Choice',
        def: 'The prospectus sets no first call date — the issuer may redeem at par at any time on 15–60 business-days notice (Strategy Inc.\'s STRD / STRF style). Treat it as permanently callable: amber when the share trades above par.' },
      { id: 'pref_par', swatch: flagSwatch('PAR $25.00', PREF.muted),
        name: 'Par Value note',
        def: 'The liquidation / redemption value per share — what a call pays and what the coupon is computed on. The quiet grey chip means no call risk is active right now (below par, or call protection still running).' },
      { id: 'pref_edgar', swatch: <span className="text-[10px] text-muted-foreground underline decoration-dotted underline-offset-2">EDGAR ↗</span>,
        name: 'Call date not yet confirmed',
        def: 'We have not confirmed the first call date from the prospectus yet. The link opens the SEC EDGAR filing search for the ticker (424B prospectus supplements) so you can read the "Optional Redemption" section yourself. Yield to Call stays blank until a date is confirmed.' },
      { id: 'pref_common', swatch: flagSwatch('COMMON: STRR', PREF.muted),
        name: 'Issuer\'s Common Stock',
        def: 'Stock Modal shortcut to the issuer\'s common shares (STRRP → STRR). Check the common\'s earnings, leverage and dividend record — that is who has to keep paying your coupon.' },
    ],
  },
  {
    title: 'Structure / Risk Flags',
    rows: [
      { id: 'leveraged', swatch: flagSwatch('LEVERAGED', 'bg-rose-500/20 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/40'),
        name: 'Leveraged ETF',
        def: 'Uses derivatives or borrowed capital to amplify returns (typically 2x or 3x). High decay risk; not buy-and-hold.' },
      { id: 'cef', swatch: flagSwatch('CEF', 'bg-violet-500/20 text-violet-700 dark:text-violet-300 ring-1 ring-violet-500/40'),
        name: 'Closed-End Fund',
        def: 'Fund that trades like a stock with a fixed share count. Often distributes high yields but can trade above / below NAV.' },
      { id: 'leveraged_cef',
        swatch: (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[9px] font-bold uppercase bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 ring-1 ring-amber-400/60">
            <Zap className="w-2.5 h-2.5" />
          </span>
        ),
        name: 'Structurally-Leveraged CEF',
        def: 'A ⚡ next to the CEF badge means this Closed-End Fund uses borrowed capital or preferred shares (typically 25-45% effective leverage) to boost distributions. Amplifies both yield AND downside risk — leverage cuts both ways. Curated set in DiviDrip covers ~96 well-known names (PDI, PDO, UTG, DSL, PTY, etc.) across PIMCO, Nuveen, BlackRock, Eaton Vance, abrdn, and other CEF sponsors. See the Leverage lens on /glossary/cef-strategies for the full list.' },
      { id: 'bdc', swatch: flagSwatch('BDC', 'bg-sky-500/20 text-sky-700 dark:text-sky-300 ring-1 ring-sky-500/40'),
        name: 'Business Development Co.',
        def: 'Specialised investment firm that lends to mid-market private companies. Required to pay out 90% of income — high yields but credit-sensitive.' },
      { id: 'spac', swatch: flagSwatch('SPAC', 'bg-slate-500/25 text-slate-700 dark:text-slate-200 ring-1 ring-slate-500/50'),
        name: 'Blank-Check / SPAC',
        def: 'Special Purpose Acquisition Company — a cash shell listed with the sole purpose of merging with a private company to take it public. SPACs pay NO dividends, generate no earnings, and hold IPO proceeds in trust until they find a target (usually 18-24 months). Ticker often ends in U/UN (a "unit" = share + warrant). Not a dividend pick.' },
      { id: 'right', swatch: flagSwatch('RIGHT', 'bg-fuchsia-500/20 text-fuchsia-800 dark:text-fuchsia-300 ring-1 ring-fuchsia-500/50'),
        name: 'SPAC Right',
        def: 'A SPAC right converts automatically into a fraction of a Class A share (commonly 1/10) when the merger closes — no exercise price, no action needed. Worthless if the SPAC liquidates instead. Pays NO dividends. The Stock Modal shows the share ticker it converts into and the 8-K filing trail.' },
      { id: 'inactive_dividend', swatch: flagSwatch('INACTIVE DIVIDEND', 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200 ring-1 ring-slate-400/60'),
        name: 'Inactive / Historical Dividend',
        def: 'The company used to pay but the last known payment is more than 18 months old, so DiviDrip hides yield, payout and ex-date metrics instead of showing stale numbers. The modal title shows the last paid date; a fresh declaration re-activates everything automatically.' },
      { id: 'mega_tier_elite', swatch: flagSwatch('MEGA TIER ELITE', PREF.amber),
        name: 'Mega Tier Elite',
        def: 'Currently on the Mega Tier Undervalued Stocks list: top-20% combined quality + value rank, at least 4 of 5 Graham checks passed, Piotroski F-Score 7+. Recomputed weekly — the badge disappears the moment the stock drops off. Tap the badge itself for the full checklist and tenure.' },
      { id: 'reit', swatch: flagSwatch('REIT', 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/40'),
        name: 'Real Estate Investment Trust',
        def: 'Owns or finances real estate (apartments, offices, warehouses, cell towers, mortgages). Required to distribute 90%+ of income, so yields of 4-10% are normal. Quality Score uses a higher safe-payout cap.' },
      { id: 'futures', swatch: flagSwatch('FUTURES', 'bg-orange-500/20 text-orange-700 dark:text-orange-300 ring-1 ring-orange-500/40'),
        name: 'Futures-based ETF',
        def: 'Holds futures contracts (oil, gas, treasuries). Subject to roll yield decay; treat as a tactical position not a long-term hold.' },
      { id: 'adr', swatch: flagSwatch('ADR', 'bg-pink-100 text-red-800 dark:bg-pink-200/90 dark:text-red-900 ring-1 ring-red-400/60'),
        name: 'American Depositary Receipt',
        def: 'A U.S.-listed certificate that represents shares of a foreign company. Trades on U.S. exchanges and pays dividends in USD (often net of foreign withholding tax). Look up the underlying company\'s home-country dividend rules.' },
      { id: 'unit', swatch: flagSwatch('UNIT', 'bg-teal-500/20 text-teal-800 dark:text-teal-300 ring-1 ring-teal-500/50'),
        name: 'Unit (share + warrant/right bundle)',
        def: 'A hybrid security that bundles one common share with a warrant and/or a right under a single ticker ending in U (JABRU, CMIIU). Almost always how a SPAC IPOs; a few young operating companies also use them as an IPO sweetener. Only the bundle trades for the first ~45–52 days, then the pieces can be split (via a broker corporate-action request, usually a flat $30–50 fee) and the share, warrant and right trade under their own symbols. SPAC units carry trust-account downside protection (~$10/share); regular-stock units do not. Pays NO dividends. The stock modal\'s Insights tab shows the bundled tickers and a full explainer.' },
      { id: 'warrant', swatch: flagSwatch('WARRANT', 'bg-amber-500/20 text-amber-800 dark:text-amber-300 ring-1 ring-amber-500/50'),
        name: 'Public Stock Warrant',
        def: 'A contract (usually SPAC-legacy) giving the holder the right to buy common stock at a fixed strike price before an expiration date, typically 2–10 years out. Pays NO dividends. Companies can force early redemption (often at $0.01/warrant) with as little as 30 days notice if the underlying stock stays above a trigger price — see the BRUNW / Boost Run case from Aug 2026. DiviDrip tracks 441+ active US warrants via Massive and runs a weekly 8-K watcher for redemption notices.' },
      { id: 'warrant_redeemed', swatch: flagSwatch('REDEEMED', 'bg-rose-500/25 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/60'),
        name: 'Warrant Redeemed / Delisted',
        def: 'The issuer has filed an 8-K exercising their early-redemption clause. Any unexercised warrants at the deadline get tendered for pennies (usually $0.01) and stop trading. The stock modal shows the exact deadline + tender price + SEC filing link once our weekly watcher detects the notice.' },
      { id: 'warrant_redemption_soon', swatch: flagSwatch('REDEEM SOON', 'bg-orange-500/20 text-orange-700 dark:text-orange-300 ring-1 ring-orange-500/60'),
        name: 'Warrant Redemption Announced',
        def: 'An early redemption 8-K has been filed but the deadline hasn\'t passed yet. Exercise or sell before the deadline — otherwise your warrants get force-tendered at the redemption price ($0.01 typical).' },
      { id: 'yield_trap', swatch: flagSwatch('YIELD TRAP', 'bg-rose-500/20 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/40'),
        name: 'Yield-Trap Detector',
        def: 'Composite score from 5 signals — high yield, stretched payout, recent cut, heavy 1-yr price decline, and heavy short interest. Score 0/5 = clean; ≥2 with at least one "hard" signal triggers the warning badge in the modal header.' },
    ],
  },
  {
    title: 'Recent Action',
    rows: [
      { id: 'cut', swatch: flagSwatch('CUT -8.2%', 'bg-rose-500/20 text-rose-700 dark:text-rose-300 ring-1 ring-rose-500/40'),
        name: 'Dividend Cut',
        def: 'The most recent declared dividend is lower than the previous payment. Number shows the magnitude of the cut.' },
      { id: 'raise', swatch: flagSwatch('+12.5% RAISE', 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/40'),
        name: 'Dividend Raise',
        def: 'The most recent declared dividend is higher than the previous payment — a positive signal for income growth.' },
      { id: 'streak', swatch: streakSwatch,
        name: 'Streak Unbroken (King / Aristocrat / Achiever raise)',
        def: "Bonus flame on the regular raise badge — a King (50y+), Aristocrat (25y+), or Achiever (10y+) just kept its multi-decade raise streak alive with a fresh hike. Stronger signal than a typical raise." },
    ],
  },
  {
    title: 'Income Risk',
    rows: [
      { id: 'risk', swatch: flagSwatch('SAFE 1/10', 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/40'),
        name: 'Risk Score (lower = safer)',
        def: '0–10 income-risk score combining payout ratio, yield extremity, structure flags, and tier. Read alongside Quality Score for a full picture.' },
    ],
  },
  {
    title: 'Watchlist Radar',
    subtitle: '· (Watchlist view only)',
    rows: [
      { id: 'exdiv_soon', swatch: flagSwatch('EX-DIV IN 3D', 'bg-sky-500/20 text-sky-700 dark:text-sky-300 ring-1 ring-sky-500/40'),
        name: 'Ex-Div ≤ 7 days',
        def: "The stock's next ex-dividend date is within the next 7 days. Buy BEFORE this date to qualify for the upcoming dividend; selling after the ex-date still earns it. The chip swaps to 'Ex-div today' or 'Ex-div tomorrow' as the date approaches." },
      { id: 'near_52w_low', swatch: flagSwatch('NEAR 52W LOW', 'bg-violet-500/20 text-violet-700 dark:text-violet-300 ring-1 ring-violet-500/40'),
        name: 'Near 52-Week Low',
        def: "The current price is within 5% of the stock's 52-week low. Often a possible buying opportunity for quality names — but always confirm fundamentals haven't deteriorated before adding (the price could be falling for a reason)." },
    ],
  },
];

// Flat lookup so the Pinned section can render rows by id in pin order.
const ROW_BY_ID = BADGE_SECTIONS.reduce((acc, s) => {
  s.rows.forEach((r) => { acc[r.id] = r; });
  return acc;
}, {});

// `quality` (no suffix) maps to the Elite row so legacy callers keep working.
const FOCUS_ALIASES = { quality: 'quality_elite' };

export function BadgeKeyContent({ focusKey }) {
  const [pinIds, setPinIds] = useState(() => loadPins());

  const togglePin = useCallback((id) => {
    setPinIds((prev) => {
      const next = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id];
      savePins(next);
      return next;
    });
  }, []);

  // Filter out any pins for ids we no longer ship (defensive for future renames).
  const validPins = pinIds.filter((id) => ROW_BY_ID[id]);
  const pinSet = new Set(validPins);
  const resolvedFocus = focusKey ? (FOCUS_ALIASES[focusKey] || focusKey) : undefined;

  return (
    <div className="space-y-0 max-h-[60vh] overflow-y-auto overflow-x-hidden" data-testid="badge-key-content">
      {validPins.length > 0 && (
        <>
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1.5 px-1 flex items-center gap-1">
            <Pin className="w-3 h-3 fill-current" />
            Pinned
          </div>
          <div data-testid="badge-key-pinned-section">
            {validPins.map((id) => {
              const row = ROW_BY_ID[id];
              return (
                <Row
                  key={`pinned-${id}`}
                  id={row.id}
                  swatch={row.swatch}
                  name={row.name}
                  def={row.def}
                  focused={resolvedFocus === row.id}
                  isPinned
                  onTogglePin={togglePin}
                />
              );
            })}
          </div>
        </>
      )}

      {BADGE_SECTIONS.map((section, idx) => (
        <div key={section.title}>
          <div className={`text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 px-1 ${idx === 0 && validPins.length === 0 ? '' : 'mt-3'}`}>
            {section.title}
            {section.subtitle ? (
              <span className="font-normal normal-case text-[10px] opacity-70"> {section.subtitle}</span>
            ) : null}
          </div>
          {section.rows.map((row) => (
            <Row
              key={row.id}
              id={row.id}
              swatch={row.swatch}
              name={row.name}
              def={row.def}
              focused={resolvedFocus === row.id}
              isPinned={pinSet.has(row.id)}
              onTogglePin={togglePin}
            />
          ))}
        </div>
      ))}

      <div className="text-[10px] text-muted-foreground italic mt-3 pt-2 border-t border-border/40 px-1">
        Tip: tap the <Pin className="inline w-2.5 h-2.5 align-middle" /> icon next to any badge to pin it to the top. Need more depth? Open the full <a href="/glossary" target="divdrip-glossary" className="text-violet-600 dark:text-violet-400 hover:underline font-semibold" title="Open the Glossary — reuses the same tab across abbreviation links">Glossary of Terms ↗</a>.
      </div>
    </div>
  );
}
